<template>
  <div class="chat-wrapper">
    <vue-scroll
      ref="scroll"
      class="chat-wrapper-scroll"
      :ops="{
        scrollPanel: { scrollingX: false, initialScrollY: '100%', speed: 0 },
        rail: { gutterOfSide: '8px' },
        bar: { disable: true },
      }"
      @handle-scroll="handleScroll"
    >
      <div
        class="chat-message-list"
        @wheel="handleWheel"
      >
        <ChatMessage
          v-for="item in items"
          :key="item.id"
          :item="item"
        />
      </div>
    </vue-scroll>
    <div class="go-to-latest-button">
      <ButtonCustom
        v-if="scrollProgress !== 1 && !scrollingFromItem"
        :text="translate('goToLatest')"
        :type="'note-button secondary-button electron-no-drag'"
        :custom-icon-name="'arrow-down'"
        :custom-icon-color="'--var(iconDefault)'"
        :custom-icon-size="'16px'"
        :button-dimensions="{ height: '24px' }"
        @click="resetScroll"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChatItem } from '@/types/chat';
import ChatMessage from '@/components/elements/ChatMessage.vue';
import { nextTick, ref, watch } from 'vue';
import ButtonCustom from '@/components/elements/ButtonCustom';
import useTranslate from '@/utilities/composables/useTranslate';

const props = defineProps({
  items: {
    type: Array<ChatItem>,
    required: true,
  },
});

const { translate } = useTranslate();

const scroll = ref(null) as any;
const enableAutoScroll = ref(true);
const scrollProgress = ref(1);
const scrollingFromItem = ref(false);
const scrollingFromItemTimeout = ref(null);

const resetScroll = () => {
  enableAutoScroll.value = true;
  scroll.value.scrollTo({ y: '100%' }, 200, 'easeInOutQuad');
};

const handleScroll = () => {
  scrollProgress.value =
    (scroll.value.getScrollProcess().v * scroll.value.$el.clientHeight) /
    scroll.value.$el.offsetHeight;
};

const handleWheel = (e) => {
  enableAutoScroll.value = scrollProgress.value === 1;

  // Cancel event when reaches end to prevent jittery behavior
  if (scrollProgress.value === 1) e.preventDefault();
};

watch(
  () => props.items,
  () => {
    if (!scroll.value || !enableAutoScroll.value) return;

    scrollingFromItem.value = true;

    if (scrollingFromItemTimeout.value) {
      clearTimeout(scrollingFromItemTimeout.value);
    }

    scrollingFromItemTimeout.value = setTimeout(() => {
      scrollingFromItem.value = false;
    }, 300);

    nextTick(() => {
      scroll.value?.scrollTo({ y: '100%' }, 200, 'easeInOutQuad');
    });
  },
  { deep: true }
);
</script>

<style lang="scss">
$goToLatestButtonHeight: 32px;

.chat-wrapper {
  @apply flex gap-2 flex-col;
  height: calc(100% - $goToLatestButtonHeight - 2px) !important;

  .go-to-latest-button {
    @apply ml-4 py-2;
    height: $goToLatestButtonHeight;
  }

  .chat-wrapper-scroll {
    .chat-message-list {
      @apply p-4 gap-y-2 flex flex-col;
    }
  }
}
</style>
